/**
 * Client-side convenience cache resolving parent -> child case IDs that
 * m16-labour-relations models as real 1:1 relations but never exposes a
 * lookup for:
 *   - no GET /conciliation?grievanceId= (only ?mediatorId=/?outcome=), and
 *     grievances.ts's own GET /grievances/:id never embeds `conciliation`
 *   - no "list settlements" route at all — the only ways to learn a
 *     Settlement's id are the POST /conciliation/:id/settlement create
 *     response, or the 409 conflict a repeat draft attempt returns
 *     (`details.settlementId`); conciliation.ts's loadConciliation() never
 *     includes `settlement` either
 *   - no GET /tribunal-cases?grievanceId= (only ?status=)
 * Every id cached here was legitimately returned by a real API response this
 * browser session observed (a creation response, a conflict error's
 * `details`, or a list/detail row's embedded relation) — this never invents
 * an id, it just remembers one so a second visit doesn't require re-deriving
 * it. See the build report for the follow-up this implies for m16: embedding
 * these relations (or adding the missing filters/a settlement list route)
 * would let the frontend drop this cache entirely.
 */
const KEY = 'labour-portal.caseLinks.v1';

interface LinkMap {
  conciliationByGrievance: Record<string, string>;
  settlementByConciliation: Record<string, string>;
  tribunalCaseByGrievance: Record<string, string>;
}

function load(): LinkMap {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as LinkMap;
  } catch {
    // malformed cache — start fresh
  }
  return { conciliationByGrievance: {}, settlementByConciliation: {}, tribunalCaseByGrievance: {} };
}

function save(map: LinkMap): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(map));
  } catch {
    // private mode / quota exceeded — cache just doesn't persist, no functional loss beyond re-deriving next time
  }
}

export function rememberConciliation(grievanceId: string, conciliationId: string): void {
  const map = load();
  map.conciliationByGrievance[grievanceId] = conciliationId;
  save(map);
}
export function lookupConciliation(grievanceId: string): string | undefined {
  return load().conciliationByGrievance[grievanceId];
}

export function rememberSettlement(conciliationId: string, settlementId: string): void {
  const map = load();
  map.settlementByConciliation[conciliationId] = settlementId;
  save(map);
}
export function lookupSettlement(conciliationId: string): string | undefined {
  return load().settlementByConciliation[conciliationId];
}

export function rememberTribunalCase(grievanceId: string, tribunalCaseId: string): void {
  const map = load();
  map.tribunalCaseByGrievance[grievanceId] = tribunalCaseId;
  save(map);
}
export function lookupTribunalCase(grievanceId: string): string | undefined {
  return load().tribunalCaseByGrievance[grievanceId];
}
